import { reactive, readonly } from 'vue';


interface DataState {
  cache: Record<string, unknown>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
  config: Record<string, unknown>;
  isAppReady: boolean;
  isAuthenticated?: boolean;
  deviceType?: 'mobile' | 'desktop';
  orientation?: 'portrait' | 'landscape';
}

const state = reactive<DataState>({
  cache: {},
  loading: {},
  errors: {},
  config: {},
  isAppReady: false,
  isAuthenticated: false,
  deviceType: undefined,
  orientation: undefined,
});

export const useStore = () => {
  const setData = <T>(key: string, data: T) => {
    state.cache[key] = data;
    state.errors[key] = null;
  };

  const getData = <T>(key: string): T | undefined => {
    return state.cache[key] as T;
  };

  const setLoading = (key: string, loading: boolean) => {
    state.loading[key] = loading;
  };

  const isLoading = (key: string) => {
    return !!state.loading[key];
  };

  const setError = (key: string, error: string | null) => {
    state.errors[key] = error;
    state.loading[key] = false;
  };

  const getError = (key: string) => {
    return state.errors[key];
  };

  const clearData = (key: string) => {
    delete state.cache[key];
    delete state.loading[key];
    delete state.errors[key];
  };

  const clearAll = () => {
    state.cache = {};
    state.loading = {};
    state.errors = {};
  };

  const setDeviceType = (type: 'mobile' | 'desktop') => {
    state.deviceType = type;
  };
  const getDeviceType = () => state.deviceType;
  const setOrientation = (orientation: 'portrait' | 'landscape') => {
    state.orientation = orientation;
  };
  const getOrientation = () => state.orientation;

  return {
    state: readonly(state),
    setData,
    getData,
    setLoading,
    isLoading,
    setError,
    getError,
    clearData,
    clearAll,
    setConfig: (configObj: Record<string, unknown>) => { state.config = { ...configObj }; },
    readConfig: <T>(key: string): T | undefined =>  state.config[key] as T,
    setAppReady: (ready: boolean) => { state.isAppReady = ready; },
    isAppReady: () => state.isAppReady,
    setAuthenticated: (authenticated: boolean) => { state.isAuthenticated = authenticated; },
    isAuthenticated: () => state.isAuthenticated,
    setDeviceType,
    getDeviceType,
    setOrientation,
    getOrientation,
  };
};
